import { formatarDocumento, type CadastroRecebidoDTO } from '@viniko/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, RotateCw } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../../componentes/ui';
import { api } from '../../lib/api';

const ESTADO: Record<CadastroRecebidoDTO['estado'], { rotulo: string; classe: string }> = {
  aguardando: { rotulo: 'Esperando o cliente', classe: 'novo' },
  vencido: { rotulo: 'Link vencido', classe: 'novo' },
  no_tiny: { rotulo: 'No Tiny', classe: 'ok' },
  erro: { rotulo: 'Não foi pro Tiny', classe: 'erro' },
};

const data = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

export function CadastrosRecebidos() {
  const qc = useQueryClient();
  const toast = useToast();
  const [aberto, setAberto] = useState<string | null>(null);
  const [reenviando, setReenviando] = useState<string | null>(null);
  const lista = useQuery({
    queryKey: ['cadastros'],
    queryFn: () => api<CadastroRecebidoDTO[]>('/api/cadastros'),
  });

  async function reenviar(id: string) {
    setReenviando(id);
    try {
      await api(`/api/cadastros/${id}/reenviar`, { metodo: 'POST' });
      await qc.invalidateQueries({ queryKey: ['cadastros'] });
      const item = qc.getQueryData<CadastroRecebidoDTO[]>(['cadastros'])?.find((c) => c.id === id);
      toast(
        item?.estado === 'no_tiny' ? 'Cadastro enviado pro Tiny' : 'O Tiny recusou de novo',
        item?.estado === 'no_tiny' ? 'ok' : 'erro',
      );
    } catch (e) {
      toast((e as Error).message, 'erro');
    } finally {
      setReenviando(null);
    }
  }

  return (
    <div className="config-cartao largo">
      <header>
        <h2>Cadastros recebidos</h2>
        <p>
          Tudo que os clientes preencheram pelo link de cadastro. Cada um já vai direto pro Tiny; se
          o Tiny recusar, fica aqui com o motivo pra reenviar.
        </p>
      </header>

      {lista.isLoading ? (
        <p className="nota-sec">Carregando…</p>
      ) : !lista.data?.length ? (
        <p className="nota-sec">
          Nenhum link enviado ainda. No chat, abra o painel do contato e toque em{' '}
          <b>Link de cadastro</b>.
        </p>
      ) : (
        <ul className="cadastros">
          {lista.data.map((c) => {
            const st = ESTADO[c.estado];
            const d = c.dados;
            return (
              <li key={c.id}>
                <div className="cadastros-linha">
                  <span className="cadastros-quem">
                    <b>{d?.nome ?? c.contatoNome}</b>
                    <small>
                      {d
                        ? `${d.tipoPessoa === 'J' ? 'CNPJ' : 'CPF'} ${formatarDocumento(d.documento)} · ${d.municipio}/${d.uf}`
                        : `Link gerado por ${c.criadoPor ?? '—'}`}
                    </small>
                  </span>
                  <small className="cadastros-data">{data(c.enviadoEm ?? c.criadoEm)}</small>
                  <span className={`tiny-status ${st.classe}`}>
                    {st.rotulo}
                    {c.estado === 'no_tiny' && c.jaExistia ? ' (já existia)' : ''}
                  </span>
                  {d && (
                    <button
                      className="icone-btn sem-borda"
                      aria-label="Ver dados"
                      aria-expanded={aberto === c.id}
                      onClick={() => setAberto(aberto === c.id ? null : c.id)}
                    >
                      <ChevronDown className={aberto === c.id ? 'virado' : ''} />
                    </button>
                  )}
                </div>
                {c.estado === 'erro' && (
                  <div className="cadastros-erro">
                    <span>{c.erro}</span>
                    <button
                      className="btn"
                      disabled={reenviando === c.id}
                      onClick={() => void reenviar(c.id)}
                    >
                      <RotateCw /> {reenviando === c.id ? 'Enviando…' : 'Reenviar pro Tiny'}
                    </button>
                  </div>
                )}
                {d && aberto === c.id && (
                  <dl className="kv cadastros-dados">
                    {d.fantasia && (
                      <>
                        <dt>Fantasia</dt>
                        <dd>{d.fantasia}</dd>
                      </>
                    )}
                    {d.tipoPessoa === 'J' ? (
                      <>
                        <dt>Inscrição estadual</dt>
                        <dd>{d.ie || '—'}</dd>
                      </>
                    ) : (
                      <>
                        <dt>RG</dt>
                        <dd>{d.rg || '—'}</dd>
                      </>
                    )}
                    <dt>Celular</dt>
                    <dd>{d.celular}</dd>
                    {d.telefone && (
                      <>
                        <dt>Telefone</dt>
                        <dd>{d.telefone}</dd>
                      </>
                    )}
                    <dt>E-mail</dt>
                    <dd>{d.email}</dd>
                    <dt>Endereço</dt>
                    <dd>
                      {[d.endereco, d.numero, d.complemento, d.bairro].filter(Boolean).join(', ')} ·{' '}
                      {d.cep.replace(/^(\d{5})(\d{3})$/, '$1-$2')}
                    </dd>
                    {c.conversaId && (
                      <>
                        <dt>Conversa</dt>
                        <dd>
                          <Link to={`/conversas/${c.conversaId}`}>Abrir conversa</Link>
                        </dd>
                      </>
                    )}
                  </dl>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
